import { useCallback, useEffect, useState } from 'react'
import { SIMULATION_SESSION_STATUSES, type SimulationSessionDto, type StartSimulationSessionRequest } from '@apna/shared'
import { api } from '@/lib/api'

function sessionsFrom(raw: unknown): SimulationSessionDto[] {
  if (typeof raw !== 'object' || raw === null || !('sessions' in raw) || !Array.isArray(raw.sessions)) throw new Error('Invalid session response')
  return raw.sessions.filter((session): session is SimulationSessionDto => typeof session === 'object' && session !== null &&
    typeof session.id === 'string' && SIMULATION_SESSION_STATUSES.includes(session.status) &&
    typeof session.startSpot === 'number' && Number.isFinite(session.startSpot) &&
    typeof session.baseVol === 'number' && Number.isFinite(session.baseVol) &&
    typeof session.seed === 'number' && typeof session.createdAt === 'string' &&
    (session.failureReason === undefined || session.failureReason === null || typeof session.failureReason === 'string'))
}

export function useSimulationSessions(enabled: boolean) {
  const [sessions, setSessions] = useState<SimulationSessionDto[]>([])
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [revision, setRevision] = useState(0)
  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    const poll = async () => {
      try {
        const raw = await api.get<unknown>('/admin/simulation-sessions/history', { signal: controller.signal })
        if (!controller.signal.aborted) { setSessions(sessionsFrom(raw)); setError(null) }
      } catch (e) {
        if (!controller.signal.aborted) setError(e instanceof Error ? e.message : 'Could not read simulation sessions')
      } finally {
        if (!controller.signal.aborted) { setLoading(false); timer = setTimeout(() => { void poll() }, 1_000) }
      }
    }
    void poll()
    return () => { controller.abort(); clearTimeout(timer) }
  }, [enabled, revision])

  const command = useCallback(async (path: string, body?: StartSimulationSessionRequest) => {
    setPending(true)
    setError(null)
    try {
      await api.post(path, body)
      setRevision((n) => n + 1)
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Session command failed')
      return false
    } finally { setPending(false) }
  }, [])
  return { sessions, loading, pending, error,
    start: (input: StartSimulationSessionRequest) => command('/admin/simulation-sessions', input),
    stop: (id: string) => command(`/admin/simulation-sessions/${id}/stop`),
  }
}
